/**
 * 🏠 **Aiming at a pit house — which cell a hit or a right-click really means**
 * (D135, T289). Pure, so a test reaches it; `main.ts` asks it before the
 * turf houses' `burDoorFor`.
 *
 * | | Hit (left button) | Right-click / O |
 * |---|---|---|
 * | the door, either half | the LOWER door — ten hits take the house down | the lower door — open or shut |
 * | a wall, the ceiling, the step | the lower door | **nothing** |
 * | the oven, the bench | the lower door | **itself** — its fire, its spinning |
 * | the earth floor | itself (unbreakable, so nothing) | nothing |
 *
 * 🔴 **A bare wall answers no menu on purpose.** The turf houses open from any
 * wall, because they are SOLID and you only ever stand outside one. A pit house
 * has an inside: an empty-handed click on its wall would swing the door shut
 * behind you, and with a torch in hand you must still be able to hang it there.
 */

import { isPitBench, isPitBlock, isPitDoor, isPitFloor, isPitOven, pitDoorFor } from '@lofi/shared';

interface Cell {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

type BlockAt = (x: number, y: number, z: number) => number;

/** ⛏️ The cell a hit on `at` is aimed at, or `undefined` for anything not a pit house (or its floor). */
export function pitBreakTarget(at: Cell, blockAt: BlockAt): Cell | undefined {
  const here = blockAt(at.x, at.y, at.z);
  if (!isPitBlock(here) || isPitFloor(here)) return undefined;
  return pitDoorFor(at, blockAt);
}

/** 🅾️ The cell whose menu a right-click on `at` opens, or `undefined` for none. */
export function pitMenuTarget(at: Cell, blockAt: BlockAt): Cell | undefined {
  const here = blockAt(at.x, at.y, at.z);
  if (isPitDoor(here)) return pitDoorFor(at, blockAt);
  if (isPitOven(here) || isPitBench(here)) return { x: at.x, y: at.y, z: at.z };
  return undefined;
}

/**
 * 🧠 **{@link pitBreakTarget}, remembered while the aim stays on one cell.**
 * `pitDoorFor` searches up to ~670 cells, and breaking asks every frame.
 *
 * 🔴 **The memory is CHECKED, not trusted, both ways**: a remembered door must
 * still be a door, and a remembered "nothing" must still be nothing — right
 * after a house is placed, the ground you were aiming at is now its wall, and a
 * stale "nothing" made hits on it do nothing (review finding 8).
 */
export function pitBreakCache(): (at: Cell, blockAt: BlockAt) => Cell | undefined {
  let last: { key: string; door: Cell | undefined } | undefined;
  return (at, blockAt) => {
    const key = `${String(at.x)},${String(at.y)},${String(at.z)}`;
    if (last?.key === key) {
      const had = last.door;
      if (had === undefined && !isPitBlock(blockAt(at.x, at.y, at.z))) return undefined;
      if (had !== undefined && isPitDoor(blockAt(had.x, had.y, had.z))) return had;
    }
    const door = pitBreakTarget(at, blockAt);
    last = { key, door };
    return door;
  };
}
